import { Link2, RefreshCw } from 'lucide-react'

import { Button, CheckboxField, StatusMessage } from '@krp/ui-kit'

import { MATCH_LABELS, serverErrorShown } from '../model/labels'
import type { InitProposal, InitResult, ShownError } from '../model/types'
import { ErrorBlock } from './parts'
import './sync.css'

export interface InitVm {
  sourceName: string
  status: 'loading' | 'failed' | 'ready'
  loadError: ShownError | null
  proposal: InitProposal | null
  /** Отмеченные пары — ключ записи источника. */
  selected: ReadonlySet<string>
  busy: boolean
  error: ShownError | null
  /** Итог последнего «Инициализировать»: создано, уже было, отказы дословно. */
  result: InitResult | null
}

export interface InitActions {
  toggle: (remoteKey: string) => void
  selectAll: (selected: boolean) => void
  confirm: () => void
  reload: () => void
}

/**
 * Инициализация связей (требования 7, план 3.5, решение 9.4): пары «запись источника ↔ товар общего каталога» с точно совпавшим
 * именем или штрихкодом. Значение, которое носят несколько записей или товаров, в пары не идёт — показано отдельно. Связи
 * создаются только по нажатию «Инициализировать».
 */
export function InitScreen({ vm, actions }: { vm: InitVm; actions: InitActions }) {
  const candidates = vm.proposal?.candidates ?? []
  const all = candidates.length > 0 && candidates.every((candidate) => vm.selected.has(candidate.remoteKey))
  const nothing = vm.selected.size === 0 ? 'Отметьте пары, которые нужно связать.' : null
  return (
    <section aria-labelledby="sync-init-title" className="sync">
      <header>
        <h2 id="sync-init-title">Инициализация связей: {vm.sourceName}</h2>
        <p className="sync__lead">
          Система нашла записи этого источника, которые совпадают с товарами общего каталога. Проверьте пары и свяжите нужные. То, что не
          связано, останется в расхождениях как «добавлено».
        </p>
      </header>

      {vm.result !== null && (
        <section className="sync__panel" aria-label="Итог инициализации" role="status">
          <p>
            Связано: {vm.result.created}. Уже были связаны: {vm.result.alreadyLinked}.{vm.result.failed.length > 0 && ` Не связано: ${vm.result.failed.length}.`}
          </p>
          {vm.result.failed.map((failure) => (
            <ErrorBlock key={failure.remoteKey} error={serverErrorShown(`Не связано: ${failure.remoteKey}`, failure.message, null, null)} />
          ))}
        </section>
      )}

      {vm.status === 'loading' && <StatusMessage kind="loading" title="Ищем совпадения…" />}
      {vm.status === 'failed' && vm.loadError !== null && (
        <ErrorBlock
          error={vm.loadError}
          action={
            <Button variant="secondary" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload} actionId="sync.init.reload">
              Повторить
            </Button>
          }
        />
      )}
      {vm.status === 'ready' && vm.proposal !== null && (
        <>
          {candidates.length === 0 ? (
            <StatusMessage kind="empty" title="Совпадений с общим каталогом не найдено." />
          ) : (
            <>
              <CheckboxField label={`Выбрать все (${candidates.length})`} checked={all} onChange={actions.selectAll} actionId="sync.init.select-all" />
              <div className="sync-table__scroll">
                <table className="sync-table">
                  <thead>
                    <tr>
                      <th scope="col">Связать</th>
                      <th scope="col">Запись источника</th>
                      <th scope="col">Товар общего каталога</th>
                      <th scope="col">Как найдено</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map((candidate) => (
                      <tr key={candidate.remoteKey}>
                        <td>
                          <CheckboxField
                            label={<span className="sync__sr">Связать «{candidate.name}»</span>}
                            checked={vm.selected.has(candidate.remoteKey)}
                            onChange={() => actions.toggle(candidate.remoteKey)}
                            actionId="sync.init.select-row"
                          />
                        </td>
                        <td>{candidate.name}</td>
                        <td>{candidate.productName}</td>
                        <td>{MATCH_LABELS[candidate.matchedBy]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {vm.proposal.ambiguous.length > 0 && (
            <section className="sync__panel" aria-label="Неоднозначные совпадения">
              <h3>Не связываются: одно значение у нескольких записей или товаров</h3>
              <p className="sync__note">Свяжите такие записи вручную в карточке общего товара или сначала разберите дубли источника.</p>
              <ul>
                {vm.proposal.ambiguous.map((entry) => (
                  <li key={`${entry.matchedBy}:${entry.value}`}>
                    {entry.value} — {MATCH_LABELS[entry.matchedBy]}: записей {entry.remoteKeys.length}, товаров {entry.productIds.length}
                  </li>
                ))}
              </ul>
            </section>
          )}
          <div className="sync__row">
            <Button
              variant="primary"
              icon={<Link2 size={16} aria-hidden="true" />}
              busy={vm.busy}
              disabled={nothing !== null}
              disabledReason={nothing}
              onClick={actions.confirm}
              actionId="sync.init.confirm"
            >
              Инициализировать ({vm.selected.size})
            </Button>
          </div>
          {vm.error !== null && <ErrorBlock error={vm.error} />}
        </>
      )}
    </section>
  )
}
